/**
 * Compact 08:00–18:00 minute track for the §7a.3 timer strip.
 * Pure presentational — entries + optional running block provided by TimerBar.
 */

const WINDOW_START_H = 8;
const WINDOW_END_H = 18;
const WINDOW_MINUTES = (WINDOW_END_H - WINDOW_START_H) * 60;

function minutesSinceWindowStart(date) {
  const d = date instanceof Date ? date : new Date(date);
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60 - WINDOW_START_H * 60;
}

function clampPct(n) {
  return Math.max(0, Math.min(100, n));
}

/**
 * @param {{ start: Date|string, end?: Date|string|null, hours?: number, billable?: boolean, id?: string }[]} entries
 * @param {{ start: Date|string }|null} running
 */
export function buildTrackBlocks(entries = [], running = null, now = new Date()) {
  const blocks = [];
  for (const e of entries) {
    if (!e?.start) continue;
    const startMin = minutesSinceWindowStart(e.start);
    let endMin;
    if (e.end) {
      endMin = minutesSinceWindowStart(e.end);
    } else if (typeof e.hours === "number") {
      endMin = startMin + e.hours * 60;
    } else {
      continue;
    }
    if (endMin <= 0 || startMin >= WINDOW_MINUTES) continue;
    const left = clampPct((Math.max(0, startMin) / WINDOW_MINUTES) * 100);
    const right = clampPct((Math.min(WINDOW_MINUTES, endMin) / WINDOW_MINUTES) * 100);
    const width = Math.max(0.4, right - left);
    blocks.push({
      id: e.id ?? `${left}-${width}`,
      left,
      width,
      kind: e.billable === false ? "non" : "billable",
    });
  }
  if (running?.start) {
    const startMin = minutesSinceWindowStart(running.start);
    const endMin = minutesSinceWindowStart(now);
    if (endMin > 0 && startMin < WINDOW_MINUTES) {
      const left = clampPct((Math.max(0, startMin) / WINDOW_MINUTES) * 100);
      const right = clampPct((Math.min(WINDOW_MINUTES, endMin) / WINDOW_MINUTES) * 100);
      blocks.push({
        id: "running",
        left,
        width: Math.max(0.8, right - left),
        kind: "running",
      });
    }
  }
  return blocks;
}

export function nowHandPct(now = new Date()) {
  return clampPct((minutesSinceWindowStart(now) / WINDOW_MINUTES) * 100);
}

export default function TimerMinuteTrack({ entries = [], running = null, now = new Date(), className = "" }) {
  const blocks = buildTrackBlocks(entries, running, now);
  const hand = nowHandPct(now);

  return (
    <div
      className={`relative h-10 flex-1 min-w-[8rem] ${className}`}
      data-testid="timer-minute-track"
      aria-hidden
    >
      <div className="absolute left-0 right-0 top-2.5 h-2.5 rounded-sm bg-brand-rule" />
      {blocks.map((b) => (
        <div
          key={b.id}
          data-kind={b.kind}
          className={`absolute top-2.5 h-2.5 rounded-sm ${
            b.kind === "billable"
              ? "bg-brand-billable"
              : b.kind === "running"
                ? "bg-brand-running rounded-r-none"
                : "bg-brand-panel"
          }`}
          style={{
            left: `${b.left}%`,
            width: `${b.width}%`,
            boxShadow: b.kind === "running" ? "inset -2px 0 0 var(--brand-brass)" : undefined,
            backgroundImage:
              b.kind === "non"
                ? "repeating-linear-gradient(135deg,var(--brand-panel) 0,var(--brand-panel) 3px,var(--brand-hairline) 3px,var(--brand-hairline) 4px)"
                : undefined,
          }}
        />
      ))}
      <div
        className="absolute top-0.5 w-0.5 h-[26px] bg-brass"
        style={{ left: `${hand}%` }}
        data-testid="timer-now-hand"
      />
      <span className="absolute left-0 top-[26px] text-[11px] text-brand-steel tabular-nums">08:00</span>
      <span className="absolute left-1/2 top-[26px] -translate-x-1/2 text-[11px] text-brand-steel tabular-nums">
        13:00
      </span>
      <span className="absolute right-0 top-[26px] text-[11px] text-brand-steel tabular-nums">18:00</span>
    </div>
  );
}
